import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Check, Star, Crown, Shield, Zap, Sword, Globe } from 'lucide-react';

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      id: 'free',
      name: 'Pro Se Starter',
      tagline: 'Perfect for those just starting their journey into self-representation and legal literacy.',
      price: 0,
      period: 'month',
      color: 'from-green-500 to-green-600',
      icon: Zap,
      badge: '🟢 FREE PLAN',
      badgeColor: 'bg-green-100 text-green-700',
      popular: false,
      features: [
        '1 Legal Matter (Permanent - Not Renewable)',
        'Educational Content Access'
      ],
      description: 'Try the platform with one free case. Upgrade for unlimited access.'
    },
    {
      id: 'basic',
      name: 'Justice Seeker',
      tagline: 'Designed for individuals with active cases, disputes, or legal projects who want deeper remedy.',
      price: 9.99,
      period: 'month',
      color: 'from-yellow-500 to-orange-600',
      icon: Shield,
      badge: '🟡 STANDARD PLAN',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      popular: true,
      features: [
        '5 Legal Matters per month (Civil & Criminal)',
        'AI Remedy Generator + Template Suite',
        '50+ Custom Templates per month',
        'AI Strategy Brain (Case Mapping Engine)',
        'Smart Response Detection (OCR + AI Analysis)',
        'AI Personal Attorney Assistant',
        'Redress Portal (Complaint Builder)',
        'AI Contract Portal (Upload & Analyze)',
        'Full Sovereign Toolbox Access (Digital Library, Video Vault, Study Quizzes, Remedy Generator)',
        'Printable Affidavits of Status, Notice to Court, and Private Trust Templates',
        'Live Chat Support (within 48 hrs)'
      ],
      description: 'For those ready to challenge false claims, build a defense, or assert private rights.'
    },
    {
      id: 'premium',
      name: 'Sovereign Shield',
      tagline: 'Your private AI legal department. For power users, family estate planners, and community leaders.',
      price: 99,
      period: 'month',
      color: 'from-red-500 to-red-600',
      icon: Crown,
      badge: '🔴 PREMIUM PLAN',
      badgeColor: 'bg-red-100 text-red-700',
      popular: false,
      features: [
        'Unlimited Legal Matters per month',
        'Unlimited Document Generation',
        'AI Strategy Brain (Case Mapping Engine)',
        'Smart Response Detection (OCR + AI Analysis)',
        'AI Personal Attorney Assistant',
        'Redress Portal (Complaint Builder)',
        'AI Contract Portal (Upload & Analyze)',
        'Bill of Complaint Generator (Post-Default)',
        'Full Sovereign Toolbox Access (Digital Library, Video Vault, Study Quizzes, Remedy Generator)',
        'Advanced AI Remedy Builder (with Trust, Estate, & Status Correction Templates)',
        'AI Document Review + Suggestions',
        'Access to Premium Content (Equity, Trust Law, Status Correction)'
      ],
      description: 'Ideal for those managing multiple cases, teaching others, or operating PMAs, Trusts, or Assemblies.'
    },
    {
      id: 'lifetime',
      name: 'Defender of the People',
      tagline: 'For those who walk the lawless lands with full armor — and never want to be bound by monthly fees.',
      price: 1497,
      period: 'once',
      color: 'from-purple-600 to-indigo-700',
      icon: Sword,
      badge: '⚔️ LIFETIME PLAN',
      badgeColor: 'bg-purple-100 text-purple-700',
      popular: false,
      features: [
        'Everything in Sovereign Shield',
        'AI Strategy Brain (Case Mapping Engine)',
        'Smart Response Detection (OCR + AI Analysis)',
        'AI Personal Attorney Assistant',
        'Redress Portal (Complaint Builder)',
        'AI Contract Portal (Upload & Analyze)',
        'Bill of Complaint Generator (Post-Default)',
        'Lifetime Access & Updates',
        'Private Forum for Lifetime Members',
        'Early Access to New Features'
      ],
      description: 'One-time payment for lifetime access to all premium features and exclusive benefits.'
    }
  ];

  const handleSelectPlan = async (planId) => {
    // Redirect to registration for free plan
    if (planId === 'free') {
      window.location.href = '/auth?mode=register';
      return;
    }

    // For paid plans, redirect to registration page with plan parameter
    window.location.href = `/register?plan=${planId}`;
  };

  return (
    <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Choose Your Path to
            <span className="bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent"> Freedom</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From first-time sovereigns to seasoned defenders, we have the right plan to support your journey toward autonomy.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {plans.map((plan) => {
            const Icon = plan.icon;
            return (
              <Card 
                key={plan.id} 
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                  plan.popular ? 'ring-2 ring-blue-500 shadow-lg' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-center py-2 text-sm font-medium">
                    MOST POPULAR
                  </div>
                )}
                
                <CardHeader className={`pb-6 ${plan.popular ? 'pt-10' : ''}`}>
                  <div className="flex justify-center mb-4">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${plan.color} flex items-center justify-center text-white`}>
                      <Icon className="w-8 h-8" />
                    </div>
                  </div>
                  
                  <Badge className={`${plan.badgeColor} mx-auto mb-3 text-xs font-medium px-3 py-1`}>
                    {plan.badge}
                  </Badge>
                  
                  <CardTitle className="text-xl font-bold text-gray-900">
                    {plan.name}
                  </CardTitle>
                  
                  <p className="text-sm text-gray-600 mt-2">
                    {plan.tagline}
                  </p>
                  
                  <div className="mt-4">
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900">
                        ${plan.price === 0 ? '0' : plan.price.toLocaleString()}
                      </span>
                      <span className="text-gray-500 ml-2">
                        /{plan.period === 'once' ? 'one-time' : plan.period}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <p className="text-xs text-gray-500 mb-6 italic">
                    {plan.description}
                  </p>
                  
                  <Button
                    onClick={() => handleSelectPlan(plan.id)}
                    className={`w-full py-2 px-4 rounded-md font-medium transition-all duration-200 ${
                      plan.id === 'free'
                        ? 'bg-gray-900 hover:bg-gray-800 text-white'
                        : plan.popular
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg'
                        : 'bg-white border-2 border-gray-900 text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {plan.id === 'free' ? 'Get Started Free' : 'Choose Plan'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            All Plans Include Our Core Promise
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <Shield className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-900">Privacy Protected</h4>
              <p className="text-sm text-gray-600">Your legal matters remain confidential and secure</p>
            </div>
            <div>
              <Crown className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-900">Constitutional Focus</h4>
              <p className="text-sm text-gray-600">Grounded in Article I Section 10 Clause 1 principles</p>
            </div>
            <div>
              <Zap className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-900">AI-Powered</h4>
              <p className="text-sm text-gray-600">Advanced legal assistance at your fingertips</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;